import "./Header.scss"
import { GiHamburgerMenu} from "react-icons/gi";
import { FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom"
import { useState, useEffect, useCallback, useRef } from 'react'

const SimpleHeader = () => {
    const [showMenu, setShowMenu] = useState(false)
    const [activeSection, setActiveSection] = useState('domu')
    const [headerHeight, setHeaderHeight] = useState(100)
    const location = useLocation()
    const navigate = useNavigate()
    const isScrollingProgrammatically = useRef(false)
    const scrollTimeoutRef = useRef(null)
    
    const sections = ['domu', 'sluzby', 'organizace', 'cenik', 'kontakt', 'recenze']
    
    // ========== Dynamické SEO meta tagy ==========
    useEffect(() => {
        const path = location.pathname === '/' ? '' : location.pathname
        const currentUrl = `https://autoskola-prikasky.cz${path}`

        // Dynamický canonical tag
        let canonical = document.querySelector('link[rel="canonical"]')
        if (!canonical) {
            canonical = document.createElement('link')
            canonical.rel = 'canonical'
            document.head.appendChild(canonical)
        }
        canonical.href = currentUrl

        // Dynamický title pro každou stránku
        const titles = {
            '/': 'Autoškola Příkaský Kyjov | Řidičský průkaz B | Rychlokurz | SUV',
            '/sluzby': 'Služby - Autoškola Příkaský Kyjov | Řidičský průkaz B, SUV kurz',
            '/organizace': 'Organizace výuky - Autoškola Příkaský Kyjov | Jak probíhá kurz',
            '/cenik': 'Ceník kurzů - Autoškola Příkaský Kyjov | Od 20.900 Kč',
            '/kontakt': 'Kontakt - Autoškola Příkaský Kyjov | Tel: 728 108 824',
            '/recenze': 'Recenze zákazníků - Autoškola Příkaský Kyjov | 5⭐ hodnocení'
        }
        document.title = titles[location.pathname] || titles['/']

        // Dynamický meta description
        const descriptions = {
            '/': 'Autoškola Příkaský v Kyjově. Řidičský průkaz B, rychlokurz, SUV kurz. 10+ let zkušeností, moderní vozový park, individuální přístup. Tel: 728 108 824',
            '/sluzby': 'Nabízíme základní kurz, expresní Solitér, SUV kurz a kondičné jízdy. Moderní vozový park Škoda Fabia a Dacia Duster. Autoškola Kyjov.',
            '/organizace': 'Jak probíhá výuka v Autoškole Příkaský Kyjov. Od registrace přes teoretickou výuku až po praktické jízdy a závěrečnou zkoušku.',
            '/cenik': 'Ceník autoškoly Kyjov: Základní kurz 20.900 Kč, Expresní Solitér 30.900 Kč, SUV kurz 25.500 Kč. Transparentní ceny bez skrytých poplatků.',
            '/kontakt': 'Kontaktujte Autoškolu Příkaský v Kyjově. Tel: 728 108 824, Email: josef.prikasky@seznam.cz, Adresa: Husova 370, 697 01 Kyjov.',
            '/recenze': 'Přečtěte si recenze od našich spokojených absolventů. Průměrné hodnocení 5⭐ na Google. 500+ úspěšných studentů z Kyjova a okolí.'
        }
        
        let metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
            metaDescription.content = descriptions[location.pathname] || descriptions['/']
        }

        // Dynamický Open Graph title
        let ogTitle = document.querySelector('meta[property="og:title"]')
        if (ogTitle) {
            ogTitle.content = titles[location.pathname] || titles['/']
        }

        // Dynamický Open Graph URL
        let ogUrl = document.querySelector('meta[property="og:url"]')
        if (ogUrl) {
            ogUrl.content = currentUrl
        }

    }, [location.pathname])
    // ========== Konec dynamických SEO meta tagů ==========

    // Dynamická výška headeru podle rozlišení
    useEffect(() => {
        const updateHeaderHeight = () => {
            const newHeight = window.innerWidth < 768 ? 80 : 100
            setHeaderHeight(newHeight)
        }

        updateHeaderHeight()
        window.addEventListener('resize', updateHeaderHeight)

        return () => window.removeEventListener('resize', updateHeaderHeight)
    }, [])

    const handleSmoothScroll = (e, targetId) => {
        e.preventDefault()
        
        // Označíme, že scrollujeme programově (kliknutím na menu)
        isScrollingProgrammatically.current = true
        
        // Najdi element a scrolluj na něj
        const element = document.getElementById(targetId)
        if (element) {
            const elementPosition = element.offsetTop
            const offsetPosition = elementPosition - headerHeight

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            })
            
            // Po dokončení scrollu resetujeme flag
            setTimeout(() => {
                isScrollingProgrammatically.current = false
            }, 1000) // 1 sekunda by měla stačit pro smooth scroll
        }
        
        // Nastav aktivní sekci
        setActiveSection(targetId)
        
        // Aktualizuj URL
        const newPath = targetId === 'domu' ? '/' : `/${targetId}`
        navigate(newPath, { replace: true })
        
        setShowMenu(false)
    }

    // Sledování scrollu pro aktivní sekci - pouze když uživatel scrolluje ručně
    const handleScroll = useCallback(() => {
        // Ignoruj scroll během programového scrollování (kliknutí na menu)
        if (isScrollingProgrammatically.current) {
            return
        }

        // Pouze sleduj scroll pokud jsme na home page nebo sekci
        const currentPath = location.pathname.replace('/', '')
        if (currentPath && !sections.includes(currentPath)) {
            return
        }

        // Debounce - aby se URL neměnila při každém scroll eventu
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current)
        }

        scrollTimeoutRef.current = setTimeout(() => {
            const scrollPosition = window.scrollY + headerHeight + 100
            let newActiveSection = 'domu'

            for (let i = sections.length - 1; i >= 0; i--) {
                const sectionId = sections[i]
                const element = document.getElementById(sectionId)
                
                if (element) {
                    const elementTop = element.offsetTop - headerHeight - 50
                    
                    if (scrollPosition >= elementTop) {
                        newActiveSection = sectionId
                        break
                    }
                }
            }

            if (newActiveSection !== activeSection) {
                setActiveSection(newActiveSection)
                
                // Aktualizuj URL pouze při změně sekce
                const newPath = newActiveSection === 'domu' ? '/' : `/${newActiveSection}`
                if (location.pathname !== newPath) {
                    navigate(newPath, { replace: true })
                }
            }
        }, 150) // 150ms debounce

    }, [activeSection, location.pathname, sections, headerHeight, navigate])

    useEffect(() => {
        const throttledHandleScroll = () => {
            // Pouze volej handleScroll pokud není programové scrollování
            if (!isScrollingProgrammatically.current) {
                handleScroll()
            }
        }

        window.addEventListener('scroll', throttledHandleScroll, { passive: true })
        
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll)
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current)
            }
        }
    }, [handleScroll])

    // Nastav aktivní sekci podle URL path - BEZ scrollování!
    useEffect(() => {
        const path = location.pathname.replace('/', '') || 'domu'
        if (sections.includes(path)) {
            // Pouze nastav aktivní položku v menu, NESCROLLUJ
            setActiveSection(path)
        }
    }, [location.pathname])

    const getNavPath = (section) => {
        return section === 'domu' ? '/' : `/${section}`
    }

    return (
        <header className="simple-header">
            <div className="header-container">
                <div className="nav-header">

                    <div className="logo">
                        <a 
                            href="/" 
                            onClick={(e) => handleSmoothScroll(e, 'domu')}
                            className="logo-link"
                        >
                            <img src="/car-tire-side.svg" alt="Autoškola Příkaský" />
                        </a>
                    </div>

                    <div className="mobile-controls">
                        <a href="tel:728108824" className="mobile-contact-btn">
                            📞
                        </a>
                        
                        <button 
                            className={`mobile-menu-btn ${showMenu ? 'active' : ''}`}
                            onClick={() => setShowMenu(!showMenu)}
                        >
                            {showMenu ? <FaTimes /> : <GiHamburgerMenu />}
                        </button>
                    </div>
                </div>

                <nav className={`navigation ${showMenu ? "show" : "hide"}`}> 
                    <ul>
                        <li>
                            <a 
                                href={getNavPath('domu')}
                                onClick={(e) => handleSmoothScroll(e, 'domu')}
                                className={activeSection === 'domu' ? 'active' : ''}
                            >
                                Domů
                            </a>
                        </li>
                        <li>
                            <a 
                                href={getNavPath('sluzby')}
                                onClick={(e) => handleSmoothScroll(e, 'sluzby')}
                                className={activeSection === 'sluzby' ? 'active' : ''}
                            >
                                Služby
                            </a>
                        </li>
                        <li>
                            <a 
                                href={getNavPath('organizace')}
                                onClick={(e) => handleSmoothScroll(e, 'organizace')}
                                className={activeSection === 'organizace' ? 'active' : ''}>
                                Organizace
                            </a>
                        </li>
                        <li>
                            <a 
                                href={getNavPath('cenik')}
                                onClick={(e) => handleSmoothScroll(e, 'cenik')}
                                className={activeSection === 'cenik' ? 'active' : ''}
                            >
                                Ceník
                            </a>
                        </li>
                        <li>
                            <a 
                                href={getNavPath('kontakt')}
                                onClick={(e) => handleSmoothScroll(e, 'kontakt')}
                                className={activeSection === 'kontakt' ? 'active' : ''}
                            >
                                Kontakt
                            </a>
                        </li>
                        <li>
                            <a 
                                href={getNavPath('recenze')}
                                onClick={(e) => handleSmoothScroll(e, 'recenze')}
                                className={activeSection === 'recenze' ? 'active' : ''}
                            >
                                Recenze
                            </a>
                        </li>
                    </ul>
                    
                    <div className="nav-contact">
                        <a href="tel:728108824" className="contact-btn">
                            728 108 824
                        </a>
                    </div>
                </nav>
            </div>
        </header>
    )
}

export default SimpleHeader